import * as tf from "@tensorflow/tfjs";
import { loadGraphModel } from "@tensorflow/tfjs-converter";
import * as constants from "../utils_new/Constants";
import "@tensorflow/tfjs-backend-cpu";
import "@tensorflow/tfjs-backend-webgl";
import "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-webgpu";
import { setWasmPaths } from "@tensorflow/tfjs-backend-wasm";

// tf.enableProdMode();
// tf.enableDebugMode();
// tf.enableDebugMode();

setWasmPaths("/wasm");

export async function setBackendExternally(backendName) {
  try {
    await tf.setBackend(backendName);
    await tf.ready();
    // console.log(`✅ Switched to backend: ${backendName}`);
    return true;
  } catch (err) {
    // console.error(`❌ Failed to switch to backend ${backendName}:`, err);
    return false;
  }
}

export class PartDetector {
  constructor() {
    this.detectionScoreThresholdForEachPart = {};
    for (let id in constants.CLASS_LABELS)
      this.detectionScoreThresholdForEachPart[constants.CLASS_LABELS[id]] = 0.7;
    this.detectionScoreThresholdForEachPart["running_board"] = 0.3;
    this.detectionScoreThresholdForEachPart["fender"] = 0.4;
    this.detectionScoreThresholdForEachPart["qtr_panel"] = 0.4;
    this.detectionScoreThresholdForEachPart["front_door"] = 0.6;
    this.detectionScoreThresholdForEachPart["window_glass"] = 0.4;
    this.detectionScoreThresholdForEachPart["wheel"] = 0.45;

    this.modelPath = "/model/model.json";
    this.motorBikeModelPath = "/model/bikeModel/model.json";
  }

  async loadModelFromURL(app) {
    await tf.ready();
    const model = await loadGraphModel(
      app === "car" ? this.modelPath : this.motorBikeModelPath
    );
    return model;
  }

  // Function to process a frame from the HTML Video Element. Reads frame in the same backend as already set.
  /**
   * It takes a video frame from the HTML Video Element, resizes it to 128x128, changes the data type
   * to 'Int32' and adds a dimension to represent the batch axis
   * @param video_element - The HTML Video Element from which the frame is to be read.
   * @returns A tensor with shape [1, 128, 128, 3]
   */

  processInput(video_element) {
    return tf.tidy(() => {
      // console.log("Before fromPixels");
      let tfimg = tf.browser.fromPixels(video_element);
      // console.log("After fromPixels");
      let resizedImg = tf.image.resizeBilinear(tfimg, [128, 128], false);
      // console.log("After resizeBilinear");
      let intImg = resizedImg.toInt();
      let expandedImg = intImg.expandDims(0);
      tfimg.dispose();
      return expandedImg;
    });
  }

  buildDetectedObjectsBufferSync(boxes_b, scores_b, classes_b) {
    const detectionObjectsDict = {};

    const boxes = boxes_b.values;
    const scores = scores_b.values;
    const classes = classes_b.values;

    for (let [i, score] of Object.entries(scores)) {
      if (
        score >
        this.detectionScoreThresholdForEachPart[
          constants.CLASS_LABELS[classes[i]]
        ]
      ) {
        if (classes[i] in detectionObjectsDict)
          if (score < detectionObjectsDict[classes[i]][1]) continue;

        const bbox = [];
        const minY = boxes[i * 4 + 0];
        const minX = boxes[i * 4 + 1];
        const maxY = boxes[i * 4 + 2];
        const maxX = boxes[i * 4 + 3];

        bbox[0] = minX;
        bbox[1] = minY;
        bbox[2] = maxX - minX;
        bbox[3] = maxY - minY;

        detectionObjectsDict[classes[i]] = [bbox, score];
      }
    }

    const detectionObjectsArr = [];

    for (let [classIdx, values] of Object.entries(detectionObjectsDict)) {
      const idx = parseInt(classIdx, 10);
      detectionObjectsArr.push({
        bbox: values[0],
        class: idx,
        label: constants.CLASS_LABELS[idx],
        score: values[1].toFixed(4),
      });
    }

    return detectionObjectsArr;
  }

  async getBikeDetectionObjects(predictions) {
    // if (Array.isArray(predictions)) {
    //   for (let i = 0; i < predictions.length; i++) {
    //     const data = await predictions[i].array();
    //   }
    // } else {
    //   const data = await predictions.array();
    // }
    const boxes = await predictions[1].array();
    const scores = await predictions[4].array();
    const classes = await predictions[0].array();

    let i = 0,
      highScore = "",
      highBox = "";
    while (i < scores[0].length) {
      if (scores[0][i] > 0.6) {
        highScore = scores[0][i];
        highBox = boxes[0][i];
        break;
      } else i++;
    }

    if (i === 100 && highScore === "") {
      return false;
    }

    return {
      highScore: highScore,
      highBox: highBox,
      index: i,
    };
  }

  getDetectionObjects(predictions) {
    const boxes = predictions[0].squeeze();
    const scores = predictions[1].squeeze();

    let boxes_processed = boxes.clipByValue(0, 1);

    const [scores_max, class_indxs] = this.filterScores(scores);

    const detections = this.buildDetectedObjectsBufferSync(
      boxes_processed.bufferSync(),
      scores_max.bufferSync(),
      class_indxs.bufferSync()
    );

    return detections;
  }

  filterScores(scores) {
    let scores_woBG = scores.slice([0, 1], [-1, -1]);

    let max_scores = scores_woBG.max(-1);

    let argmax_scores = scores_woBG.argMax(-1).add(1);

    return [max_scores, argmax_scores];
  }
}
